import React, { useState } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { Select, MenuItem } from '@mui/material';
import { TimeSeriesDataPoint } from 'server/api/services/analytics/types';
import { abbreviateNum } from 'lib/helpers';
import { formatDate } from 'lib/utils/datetime';
import { TitleWithToggle } from 'components/TitleWithToggle';

interface StatsOverTimeProps {
  data: TimeSeriesDataPoint[];
  title: string;
  subtitle?: string | React.ReactElement;
  previewTitle?: string | React.ReactElement;
  loading?: boolean;
  processingMessage?: string;
  defaultStat?: keyof Pick<TimeSeriesDataPoint, 'likes' | 'combinedViews' | 'shares' | 'comments' | 'count' | 'impressions'>;
}

const statOptions = [
  { key: 'likes', label: 'Likes' },
  { key: 'combinedViews', label: 'Views' },
  { key: 'impressions', label: 'Impressions' },
  { key: 'shares', label: 'Shares' },
  { key: 'comments', label: 'Comments' },
  { key: 'count', label: 'Posts' },
] as const;

export const StatsOverTime: React.FC<StatsOverTimeProps> = ({
  data,
  title,
  subtitle,
  previewTitle,
  loading = false,
  processingMessage,
  defaultStat = 'likes',
}) => {
  const [selectedStat, setSelectedStat] = useState(defaultStat);

  if (loading) {
    return (
      <div className="bg-white dark:bg-appbg-dark rounded-lg shadow p-4 md:p-6">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-1/3 mb-2" />
          <div className="h-4 bg-gray-200 rounded w-1/2 mb-4" />
          <div className="h-64 bg-gray-200 rounded" />
        </div>
        {processingMessage && (
          <div className="mt-4 text-center text-mediumgray dark:text-hintgray text-sm">
            {processingMessage}
          </div>
        )}
      </div>
    );
  }

  if (!data || data.length === 0) {
    return null;
  }

  // Sort data by date
  const sortedData = [...data].sort(
    (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime(),
  );

  const formatYAxis = (value: number) => {
    if (typeof value !== 'number' || Number.isNaN(value)) return '0';
    return abbreviateNum(value, 1);
  };

  return (
    <div className="bg-white dark:bg-appbg-dark rounded-lg shadow p-4 md:p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold">{title}</h3>
          {subtitle && (
            <TitleWithToggle
              fullTitle={subtitle}
              exportTitle={subtitle.toString()}
              previewTitle={previewTitle}
              className="text-mediumgray dark:text-hintgray text-sm mt-1"
            />
          )}
        </div>
        <Select
          value={selectedStat}
          onChange={(e) => setSelectedStat(e.target.value as typeof selectedStat)}
          size="small"
          className="min-w-[120px]"
        >
          {statOptions.map((option) => (
            <MenuItem key={option.key} value={option.key}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </div>

      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={sortedData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis
              dataKey="date"
              tickFormatter={(value) => formatDate(value, true)}
              tick={{ fontSize: 12 }}
              stroke="#666"
            />
            <YAxis
              tickFormatter={formatYAxis}
              tick={{ fontSize: 12 }}
              stroke="#666"
            />
            <Tooltip
              content={({ active, payload, label }) => {
                if (active && payload && payload.length) {
                  const pointData = payload[0].payload;
                  return (
                    <div className="bg-white dark:bg-appbg-dark border border-gray-300 rounded-lg p-3 shadow-lg">
                      <p className="font-semibold text-sm mb-2">
                        {formatDate(label, true)}
                      </p>
                      <div className="space-y-1 text-sm">
                        <div className="flex justify-between gap-2">
                          <span className="text-gray-600 dark:text-gray-400">Likes:</span>
                          <span className="font-medium">{pointData.likes?.toLocaleString() || '0'}</span>
                        </div>
                        <div className="flex justify-between gap-2">
                          <span className="text-gray-600 dark:text-gray-400">Views:</span>
                          <span className="font-medium">{pointData.combinedViews?.toLocaleString() || '0'}</span>
                        </div>
                        <div className="flex justify-between gap-2">
                          <span className="text-gray-600 dark:text-gray-400">Impressions:</span>
                          <span className="font-medium">{pointData.impressions?.toLocaleString() || '0'}</span>
                        </div>
                        <div className="flex justify-between gap-2">
                          <span className="text-gray-600 dark:text-gray-400">Shares:</span>
                          <span className="font-medium">{pointData.shares?.toLocaleString() || '0'}</span>
                        </div>
                        <div className="flex justify-between gap-2">
                          <span className="text-gray-600 dark:text-gray-400">Comments:</span>
                          <span className="font-medium">{pointData.comments?.toLocaleString() || '0'}</span>
                        </div>
                        <div className="flex justify-between gap-2">
                          <span className="text-gray-600 dark:text-gray-400">Posts:</span>
                          <span className="font-medium">{pointData.count?.toLocaleString() || '0'}</span>
                        </div>
                      </div>
                    </div>
                  );
                }
                return null;
              }}
            />
            <Line
              type="linear"
              dataKey={selectedStat}
              stroke="#0E8C8D"
              strokeWidth={2}
              dot={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};
